/**
 * Activity Panel Component
 *
 * Composite component that combines ActivityTimeline and CommentThread.
 * Provides tabbed interface to switch between activity and comments.
 *
 * Features:
 * - Tabs to switch between "Activity" and "Comments"
 * - Passes jobId to both child components (they handle their own data fetching)
 * - Responsive design
 */

import * as React from 'react';
import { ActivityTimeline } from './ActivityTimeline';
import { CommentThread } from './CommentThread';
import { cn } from '@/lib/utils';

export interface ActivityPanelProps {
  jobId: string;
}

type TabId = 'activity' | 'comments';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
}

const tabs: Tab[] = [
  {
    id: 'activity',
    label: 'Activity',
    icon: '📋',
  },
  {
    id: 'comments',
    label: 'Comments',
    icon: '💬',
  },
];

export function ActivityPanel({ jobId }: ActivityPanelProps) {
  const [activeTab, setActiveTab] = React.useState<TabId>('activity');

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav
          className="-mb-px flex space-x-8"
          aria-label="Activity tabs"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
                aria-current={isActive ? 'page' : undefined}
                role="tab"
                aria-selected={isActive}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div role="tabpanel">
        {activeTab === 'activity' && (
          <ActivityTimeline jobId={jobId} />
        )}
        {activeTab === 'comments' && (
          <CommentThread jobId={jobId} />
        )}
      </div>
    </div>
  );
}
